/**
 * @fileoverview Syndicator Showdown — Notification helpers
 * @module utils/notificationHelpers
 *
 * Small, dependency-free helpers shared by the notification toast/card/center
 * components. Kept separate from the components so future notification
 * producers (Phase 3+ triggers, dev scripts) can reuse the same status/
 * artwork rules without importing React.
 */

// Canonical notification types this phase renders. Future producers can add
// new types — anything unmapped just falls back to the "system" treatment.
export const NOTIFICATION_TYPES = {
  RUNNER_UPCOMING:     'RUNNER_UPCOMING',
  RACE_RESULT:         'RACE_RESULT',
  STABLE_SHARE:        'STABLE_SHARE',
  ACHIEVEMENT_UNLOCKED: 'ACHIEVEMENT_UNLOCKED', // Phase 8
  SYSTEM:              'SYSTEM',
};

// Legacy docs (welcome/broadcast) written before Phase 2 only have `type` +
// `read`, no `imageType`/`status`. Map them onto the new visual system.
// RACE_RESULT maps to the generic 'result' bucket here — resolveImageType()
// below splits it into 'result_win' / 'result_top5' using the payload, since
// both a 1st-place win and a 2nd-5th placing share the same Firestore
// `imageType` but need two different artworks.
const IMAGE_TYPE_BY_LEGACY_TYPE = {
  welcome:              'system',
  broadcast:            'system',
  RUNNER_UPCOMING:      'upcoming',
  RACE_RESULT:          'result',
  STABLE_SHARE:         'share',
  ACHIEVEMENT_UNLOCKED: 'achievement', // Phase 8
  SYSTEM:               'system',
};

// Fixed premium background artwork per notification type (web/public/*).
// Every one of these is layered under a dark navy gradient overlay in
// NotificationCard.css for readable white/silver text — see
// getNotificationArtwork()'s null fallback for the "asset missing" case,
// which collapses gracefully to the plain branded dark gradient card.
// 'achievement' (Phase 8) has no dedicated background asset yet — falls
// back to that same plain branded card via the || null in
// getNotificationArtwork() below.
const ARTWORK_BY_IMAGE_TYPE = {
  share:       '/stable-share-dark.png',
  upcoming:    '/upcoming-dark.png',
  result_win:  '/top1-dark.png',
  result_top5: '/top5-dark.png',
  achievement: null,
  system:      null,
};

const ICON_BY_IMAGE_TYPE = {
  share:       '📨',
  upcoming:    '🚦',
  result_win:  '🏆',
  result_top5: '🎖️',
  achievement: '🏆',
  system:      '📢',
};

const EYEBROW_BY_IMAGE_TYPE = {
  share:       'Share Your Stable',
  upcoming:    'Upcoming Runner',
  result_win:  'Showdown Result',
  result_top5: 'Showdown Result',
  achievement: 'Achievement Unlocked',
  system:      'Announcement',
};

/**
 * Resolves the effective imageType for a notification doc, with legacy
 * fallback. Race results are stored with a single `imageType: 'result'` but
 * render one of two artworks depending on whether the player's best result
 * this race was a win (1st) or a paid placing (2nd-5th) — see
 * resolveResultSplit().
 */
export function resolveImageType(notification) {
  if (!notification) return 'system';
  if (notification.imageType && ARTWORK_BY_IMAGE_TYPE[notification.imageType] !== undefined) {
    return resolveResultSplit(notification, notification.imageType);
  }
  return resolveResultSplit(notification, IMAGE_TYPE_BY_LEGACY_TYPE[notification.type] || 'system');
}

/**
 * Splits the generic 'result' imageType into 'result_win' (1st place,
 * top1-dark.png) vs 'result_top5' (2nd-5th, top5-dark.png) using
 * payload.bestResult.finishingPosition — present on every real RACE_RESULT
 * notification (createRaceResultNotifications.js) and every dev-test preset
 * (createTestNotification.js). Defaults to the top5/placing treatment when
 * the position can't be determined, since "you earned a prize" is the safer
 * assumption than claiming a win that didn't happen.
 */
function resolveResultSplit(notification, imageType) {
  if (imageType !== 'result') return imageType;
  const position = notification.payload?.bestResult?.finishingPosition;
  return position === 1 ? 'result_win' : 'result_top5';
}

export function getNotificationArtwork(imageType) {
  return ARTWORK_BY_IMAGE_TYPE[imageType] || null;
}

export function getNotificationIcon(imageType) {
  return ICON_BY_IMAGE_TYPE[imageType] || ICON_BY_IMAGE_TYPE.system;
}

export function getNotificationEyebrow(imageType) {
  return EYEBROW_BY_IMAGE_TYPE[imageType] || EYEBROW_BY_IMAGE_TYPE.system;
}

/**
 * Derives a normalised status ("unread" | "read" | "dismissed") for a
 * notification doc, understanding both the new `status` field and the
 * legacy `read` boolean (pre-Phase-2 welcome/broadcast docs).
 */
export function deriveStatus(notification) {
  if (!notification) return 'read';
  if (notification.status) return notification.status;
  return notification.read ? 'read' : 'unread';
}

export function toMs(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function isExpired(notification) {
  const expiresAtMs = toMs(notification?.expiresAt);
  return expiresAtMs !== null && expiresAtMs <= Date.now();
}

/** Relative "time ago" label for the notification center list. */
export function formatRelativeTime(val) {
  const ms = toMs(val);
  if (!ms) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(ms).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
